import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Filter, Bug, Lightbulb, RefreshCw } from 'lucide-react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { Button } from '../ui/button';
import { KanbanTicketCard, createRenderTicketClone } from './KanbanTicketCard';
import { TicketForm } from './TicketForm';
import { TicketDetail } from './TicketDetail';
import {
  Ticket,
  TicketFilters,
  TicketType,
  TicketPriority,
  TicketStatus,
  TicketArea,
  CreateTicketRequest,
  UpdateTicketRequest,
  ticketStatusLabels,
  ticketStatusColors,
  ticketPriorityLabels,
  ticketAreaLabels
} from '../../types/ticket';
import { getTickets, createTicket, updateTicket } from '../../services/ticketsApi';
import socketService from '../../services/socket';
import { useUser } from '../../contexts/UserContext';

const STATUSES: TicketStatus[] = ['aberto', 'em_analise', 'em_desenvolvimento', 'resolvido', 'fechado'];

interface TicketsTabProps {
  projectId: string;
  serviceOrderId?: string;
}

export const TicketsTab: React.FC<TicketsTabProps> = ({ projectId, serviceOrderId }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TicketFilters>({});
  const { user } = useUser();

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveFilters = { ...filters };
      if (serviceOrderId) {
        effectiveFilters.serviceOrderId = serviceOrderId;
      }
      // Remove status filter since columns handle status separation
      delete effectiveFilters.status;
      const data = await getTickets(projectId, effectiveFilters);
      setTickets(data);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, serviceOrderId, filters]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Socket.io real-time updates
  useEffect(() => {
    const handleCreated = (ticket: Ticket) => {
      if (serviceOrderId && ticket.serviceOrderId !== serviceOrderId) return;
      setTickets(prev => [ticket, ...prev]);
    };

    const handleUpdated = (ticket: Ticket) => {
      setTickets(prev => prev.map(t => t._id === ticket._id ? ticket : t));
    };

    const handleDeleted = ({ ticketId }: { ticketId: string }) => {
      setTickets(prev => prev.filter(t => t._id !== ticketId));
    };

    socketService.on('ticket:created', handleCreated);
    socketService.on('ticket:updated', handleUpdated);
    socketService.on('ticket:deleted', handleDeleted);

    return () => {
      socketService.off('ticket:created', handleCreated);
      socketService.off('ticket:updated', handleUpdated);
      socketService.off('ticket:deleted', handleDeleted);
    };
  }, [serviceOrderId]);

  const handleCreate = async (data: CreateTicketRequest) => {
    await createTicket(projectId, data);
    loadTickets();
  };

  const handleUpdate = async (data: UpdateTicketRequest) => {
    if (!editingTicket) return;
    await updateTicket(projectId, editingTicket._id, data);
    setEditingTicket(null);
    loadTickets();
  };

  const handleEdit = (ticket: Ticket) => {
    setEditingTicket(ticket);
    setShowDetail(null);
  };

  const handleTicketDeleted = () => {
    loadTickets();
  };

  // Drag and drop handler for moving tickets between status columns
  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    const newStatus = destination.droppableId as TicketStatus;

    // Optimistic update
    setTickets(prev => prev.map(t =>
      t._id === draggableId ? { ...t, status: newStatus } : t
    ));

    try {
      await updateTicket(projectId, draggableId, { status: newStatus });
    } catch (err) {
      // Revert on error
      loadTickets();
    }
  };

  // Group tickets by status
  const ticketsByStatus = Object.fromEntries(
    STATUSES.map(status => [
      status,
      tickets.filter(t => t.status === status)
    ])
  ) as Record<TicketStatus, Ticket[]>;

  // Count by type
  const bugCount = tickets.filter(t => t.tipo === 'bug').length;
  const melhoriaCount = tickets.filter(t => t.tipo === 'melhoria').length;

  // Flatten all tickets for renderClone lookup
  const allTickets = tickets;

  return (
    <div className="flex flex-col h-full min-h-0 gap-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-xl font-semibold text-foreground-primary">
            {serviceOrderId ? 'Chamados da OS' : 'Chamados'}
          </h2>
          <div className="flex items-center gap-4 mt-2 text-sm text-foreground-secondary">
            <span className="flex items-center gap-1">
              <Bug className="w-4 h-4 text-red-400" />
              {bugCount} bugs
            </span>
            <span className="flex items-center gap-1">
              <Lightbulb className="w-4 h-4 text-yellow-400" />
              {melhoriaCount} melhorias
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadTickets}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Chamado
          </Button>
        </div>
      </div>

      {/* Filters (without status filter - columns handle that) */}
      {showFilters && (
        <div className="bg-background-tertiary border border-white/10 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Tipo</label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => setFilters({ ...filters, tipo: e.target.value as TicketType || undefined })}
                className="w-full px-3 py-2 bg-background-secondary border border-white/10 rounded text-foreground-primary text-sm"
              >
                <option value="">Todos</option>
                <option value="bug">Bug</option>
                <option value="melhoria">Melhoria</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Prioridade</label>
              <select
                value={filters.prioridade || ''}
                onChange={(e) => setFilters({ ...filters, prioridade: e.target.value as TicketPriority || undefined })}
                className="w-full px-3 py-2 bg-background-secondary border border-white/10 rounded text-foreground-primary text-sm"
              >
                <option value="">Todas</option>
                {Object.entries(ticketPriorityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Area</label>
              <select
                value={filters.area || ''}
                onChange={(e) => setFilters({ ...filters, area: e.target.value as TicketArea || undefined })}
                className="w-full px-3 py-2 bg-background-secondary border border-white/10 rounded text-foreground-primary text-sm"
              >
                <option value="">Todas</option>
                {Object.entries(ticketAreaLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button variant="outline" size="sm" onClick={() => setFilters({})}>
              Limpar Filtros
            </Button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex flex-1 min-h-0 gap-4 overflow-x-auto pb-4">
            {STATUSES.map(status => {
              const columnTickets = ticketsByStatus[status];
              const count = columnTickets.length;

              return (
                <div
                  key={status}
                  className="flex-shrink-0 flex flex-col bg-background-secondary/50 border border-white/10 rounded-lg h-full overflow-hidden"
                  style={{ minWidth: '280px', width: '280px' }}
                >
                  {/* Column colored top border */}
                  <div
                    className="h-1 rounded-t-lg flex-shrink-0"
                    style={{ backgroundColor: ticketStatusColors[status] }}
                  />

                  {/* Column Header */}
                  <div className="px-3 py-2.5 border-b border-white/10 flex-shrink-0">
                    <h3 className="text-sm font-semibold text-foreground-primary">
                      {ticketStatusLabels[status]}{' '}
                      <span className="text-foreground-tertiary font-normal">({count})</span>
                    </h3>
                  </div>

                  {/* Droppable Area */}
                  <Droppable
                    droppableId={status}
                    renderClone={createRenderTicketClone(allTickets, () => {})}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`
                          flex-1 p-2 min-h-[200px] overflow-y-auto transition-colors
                          ${snapshot.isDraggingOver ? 'bg-blue-500/10' : ''}
                        `}
                      >
                        {columnTickets.length === 0 ? (
                          <div className="flex items-center justify-center h-full min-h-[100px]">
                            <p className="text-xs text-foreground-tertiary">Nenhum chamado</p>
                          </div>
                        ) : (
                          columnTickets.map((ticket, index) => (
                            <KanbanTicketCard
                              key={ticket._id}
                              ticket={ticket}
                              index={index}
                              onClick={() => setShowDetail(ticket._id)}
                            />
                          ))
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* Form Modal */}
      {(showForm || editingTicket) && (
        <TicketForm
          projectId={projectId}
          ticket={editingTicket || undefined}
          serviceOrderId={serviceOrderId}
          onSubmit={async (data) => {
            if (editingTicket) {
              await handleUpdate(data as UpdateTicketRequest);
            } else {
              await handleCreate(data as CreateTicketRequest);
            }
          }}
          onClose={() => {
            setShowForm(false);
            setEditingTicket(null);
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetail && user && (
        <TicketDetail
          projectId={projectId}
          ticketId={showDetail}
          currentUserId={user._id}
          onClose={() => setShowDetail(null)}
          onEdit={handleEdit}
          onDelete={handleTicketDeleted}
        />
      )}
    </div>
  );
};
